import * as React from 'react';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DateTimePicker from '@mui/lab/DateTimePicker';
import config from '../../../app/config';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Paper from '@mui/material/Paper';
import useDict from '../../../hooks/useDict';
import {setLoading} from '../../../store/reducers/patient';
import {useDispatch} from 'react-redux';
import Chip from '@mui/material/Chip';
import {DatabaseIconHL7, DateIconHL7} from '../../../components/icons/icons';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import _ from 'lodash';
import {RowMessage, useTableStyles} from './att_utils';
import {AttendanceAPI} from './attendances_api';
import {useWebSocket} from '../../../context/WebSocketProvider';

const columnsWidths = {
    'Info': '100px',
    'Date': '300px',
    'Created': '300px',
    'Type': '170px',
    'Hospital number': '170px',
    'Attendance': '200px',
    'System': '200px',
    'Version': '200px',
    'Actions': '200px',
    'Patient': '200px',
    'Status': '170px',
    'Last Send Time': '200px',
    'Last Status': '170px',
    'Hl7 Type': '170px',
};



const AttendancesTable= forwardRef((props, ref) => {

    const isEyonix = process.env.BUILD_MODULE === "main";
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const controller = new AbortController();
    const classes = useTableStyles();

    const [runSocket, setRunSocket] = React.useState(props.liveMessage);
    const {manageSubscription, message} = useWebSocket();

    const [messageType] = useDict('HL7MessageTypes');

    useEffect(() => {
        manageSubscription([props.webSocketChanel], runSocket)
    }, [runSocket, props.webSocketChanel]);

    useImperativeHandle(ref, () => ({
        refreshData: () => {
            applyFilter();
        },
    }));

    const [messages, setMessages] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [sortSend, setSortSend] = React.useState(null);
    const [sortOrder, setSortOrder] = React.useState(false);

    const [selectedRows, setSelectedRows] = React.useState([]);

    const pageSize = 20;

    useEffect(() => {
        if (props.columns.length) {
            let defaultSortItem = _.find(props.columns, {defaultSort: true});
            if (defaultSortItem) {
                setSortSend(defaultSortItem.value);
            } else {
                setSortSend('!publishDate');
            }
        }

    }, [props.columns]);

    const [searchCriteria, setSearchCriteria] = React.useState({
        searchFromDay: moment().subtract(1, 'month'),
        searchToDay: moment(),
        catalog: null,
        procedure: null,
        technician: null,
        specialty: null,
        location: null,
        admissionType: null,
        paymentClass: null,
    });


    useEffect(() => {
        if (props.liveMessage) {
            setRunSocket(props.liveMessage);
            getDocReportData();
        } else {
            setRunSocket(props.liveMessage);
        }

    }, [props.liveMessage]);


    useEffect(() => {
        getDocReportData();
    }, [message]);


    const getFilter = (_page, _pageSize) => {
        let filter = {
            dateFrom : moment(searchCriteria.searchFromDay).format("YYYY-MM-DD HH:mm"),
            dateTo :  moment(searchCriteria.searchToDay).format("YYYY-MM-DD HH:mm")
        };
        return filter;
    };

    const getDocReportData = () => {
        if (sortSend) {
            dispatch(setLoading(true));
            getSetFilters();
            AttendanceAPI.getAttendances(getFilter(page, pageSize)).then(tableData => {
                if (tableData?.data) {
                    setMessages(tableData.data);
                }
            }).finally(() => {
                dispatch(setLoading(false));
            });
        }
    };

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    const [filtersChip, setFilterChips] = React.useState([]);

    const applyFilter = () => {
        if (!page) {
            getDocReportData();
        } else
            setPage(0);
    };

    const getSetFilters = () => {
        let filters = [];

        if (searchCriteria.catalog) filters.push({filed: 'catalog', value: searchCriteria.catalog});
        if (searchCriteria.procedure) filters.push({
            filed: 'procedure',
            value: searchCriteria.procedure,
        });
        if (searchCriteria.technician) filters.push({
            filed: 'technician',
            value: searchCriteria.technician,
        });
        if (searchCriteria.specialty) filters.push({filed: 'specialty', value: searchCriteria.specialty});
        if (searchCriteria.location) filters.push({filed: 'location', value: searchCriteria.location});

        setFilterChips(filters);
    };

    useEffect(() => {
        if (doSearch.current) {
            applyFilter();
            doSearch.current = false;
        }
    }, [searchCriteria]);

    const doSearch = useRef(false);

    const handleDelete = (chip) => {
        doSearch.current = true;
        setSearchCriteria(prevState => ({...prevState, [chip.filed]: ''}));
    };

    const renderFilters = () => {
        return (<Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{padding: '40px', borderRadius: '5px', marginBottom: '10px', border: '1px solid #448FFF'}}
        >
            <Grid item xs={6}>
                <DateTimePicker
                    label={t('Date From')}
                    inputFormat={config.defaultDateTimeFormat}
                    inputVariant="outlined"
                    clearable={true}
                    ampm={false}
                    value={searchCriteria.searchFromDay}
                    onChange={(value) => {
                        setSearchCriteria(prevState => ({...prevState, searchFromDay: value}));
                    }}
                    renderInput={(inp) => <TextField style={{width: '100%', margin: 0}} {...inp}
                                                     size={props.size}/>}
                />
            </Grid>
            <Grid item xs={6}>
                <DateTimePicker
                    label={t('Date To')}
                    inputFormat={config.defaultDateTimeFormat}
                    inputVariant="outlined"
                    clearable={true}
                    ampm={false}
                    value={searchCriteria.searchToDay}
                    onChange={(value) => {
                        setSearchCriteria(prevState => ({...prevState, searchToDay: value}));
                    }}
                    renderInput={(inp) => <TextField style={{width: '100%', margin: 0}} {...inp}
                                                     size={props.size}/>}
                />
            </Grid>
            <Grid item xs={12}>
                <Button variant="contained" className={classes.agyleButton} onClick={applyFilter}>Apply</Button>
            </Grid>
            <Grid item xs={12}>
                {filtersChip.map((filter) => {
                    return <Chip label={filter.value} onDelete={() => handleDelete(filter)}/>;
                })}
            </Grid>
        </Grid>);
    };


    const addRow = (row) => {
        setSelectedRows((prRows) => ([...prRows, row]));
    };

    const removeRow = (row) => {
        let selectedRowsTmp = selectedRows.filter(item => item !== row);
        setSelectedRows(selectedRowsTmp);
    };

    useEffect(() => {
        if (sortSend)
            getDocReportData();
    }, [sortSend, sortOrder, page]);

    const getColumnWidth = column => (columnsWidths[column?.description] || '256px');


    const handleSetSort = (column) => {
        if (column.value === sortSend) {
            setSortOrder(!sortOrder);
        } else {
            setSortSend(column.value);
        }
    };

    const getSortIcon = (columnName) => {
        if (columnName !== sortSend) return '';
        if (sortOrder) return <ArrowDropDownIcon/>;
        return <ArrowDropUpIcon/>;

    };
    return (<div>
        {!props.filterHidden && renderFilters()}

        <Paper sx={{width: '100%', overflow: 'hidden', mt: 4}}>
            <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead key={'HEAD'}>
                        <TableRow>
                            {props.columns.map((column, indexColumn) => {
                                return (
                                    <TableCell
                                        key={indexColumn + 'Hasd'}
                                        align={column.align}
                                        style={{
                                            minWidth: column.minWidth,
                                            width: column.maxWidth || getColumnWidth(column),
                                            border: '1px solid #80808035',
                                        }}
                                    >
                                        <Grid container justifyContent="space-between">
                                            <Typography variant="subtitle2"
                                                        color="textSecondary"
                                                        style={{cursor: column.sort ? 'pointer' : ''}}
                                                        onClick={() => {
                                                            if (column.sort) {
                                                                handleSetSort(column);
                                                            }
                                                        }}>
                                                <div style={{display: 'flex'}}>
                                                    {column.description} {getSortIcon(column.value)}
                                                </div>

                                            </Typography>
                                            {indexColumn === props.columns.length - 1 &&
                                                <DatabaseIconHL7 onClick={props.openColumnManager} style={{cursor: "pointer"}}/>}
                                            {column.name === 'dateTime' && <DateIconHL7/>}
                                        </Grid>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {messages?.map((row, rowIndex) => {
                            // console.log("test", row);
                            return <React.Fragment key={rowIndex + 'div'}>
                                <RowMessage columns={props.columns}
                                            messagePath={props.messagePath}
                                            row={row} handleAddRow={addRow}
                                            rowIndex={rowIndex}
                                            outcoming={props.outcoming || false}
                                            handleRemoveRow={removeRow}/>
                            </React.Fragment>;
                        })}
                    </TableBody>
                    {/*<TableFooter>*/}
                    {/*    <TableRow>*/}
                    {/*        <TableCell colSpan={8}>*/}
                    {/*            <IconButton size="small"*/}
                    {/*                        disabled={page < 1}*/}
                    {/*                        onClick={handleChangePrevPage}>*/}
                    {/*                <NavigateBeforeSharpIcon fontSize="small"/>*/}
                    {/*            </IconButton>*/}
                    {/*            Page: {page + 1} from {allPages}*/}
                    {/*            <IconButton size="small"*/}
                    {/*                        disabled={(page+1) >= allPages}*/}
                    {/*                        onClick={handleChangeNextPage}>*/}
                    {/*                <NavigateNextIcon fontSize="small"/>*/}
                    {/*            </IconButton>*/}
                    {/*        </TableCell>*/}
                    {/*    </TableRow>*/}
                    {/*</TableFooter>*/}
                </Table>
            </TableContainer>
        </Paper>

    </div>);
})

export default AttendancesTable;